import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchLcr, patchLcr, publishLcr, fetchLcrVersions,
  fetchPreviousRecords, requestLcrAccess, removeLcrAttachment,
  lcrAiDraft, lcrAiImprove, lcrAiSuggest, lcrReadiness,
  uploadLcrAttachment, fetchLcrPreview,
  fetchLcrTemplates, saveLcrTemplate, deleteLcrTemplate,
} from "@lawyer/services/lawyerApi";
import {
  Sparkles, Wand2, Save, Send, History, ArrowLeft, Loader2, Upload, BookmarkPlus,
} from "lucide-react";

import { OUTCOME_LABELS, NEXT_STEP_PRESETS, EMPTY, buildForm } from "@lawyer/components/lcr/lcrConstants";
import { StateBadge, Row, ImproveButton, RichArea, BulletList, DocList, StepList, FollowUpBlock, OutcomePicker } from "@lawyer/components/lcr/LcrFields";
import { PreviousRecordsPanel, SuggestionsPanel, TemplateBar, AttachmentsBlock } from "@lawyer/components/lcr/LcrSidePanels";
import { PreCheckModal } from "@lawyer/components/lcr/LcrPreCheckModal";
import { PreviewModal } from "@lawyer/components/lcr/LcrPreviewModal";

export default function LawyerLCR() {
  const { cid } = useParams();
  const [state, setState] = useState({ loading: true, error: null });
  const [lcr, setLcr] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiBusy, setAiBusy] = useState({ draft: false, improve: null, suggest: false });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(true);
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [previousCtx, setPreviousCtx] = useState({ granted: [], available: [] });
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [checkReport, setCheckReport] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    // Secondary panels don't depend on the record itself — start them concurrently with fetchLcr.
    const secondary = Promise.all([
      fetchLcrVersions(cid).catch(() => ({ items: [] })),
      fetchPreviousRecords(cid).catch(() => ({ granted: [], available: [] })),
      fetchLcrTemplates().catch(() => ({ items: [] })),
    ]);
    try {
      const data = await fetchLcr(cid);
      setLcr(data.lcr);
      setConsultation(data.consultation);
      setForm(buildForm(data.lcr));
      setAttachments(data.lcr?.attachments || []);
      setState({ loading: false, error: null });
      secondary.then(([v, p, t]) => {
        setVersions(v.items || []);
        setPreviousCtx({ granted: p.granted || [], available: p.available || [] });
        setTemplates(t.items || []);
      });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "Could not load consultation" });
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const isPublished = lcr?.state === "published";

  // ---------- AI actions ----------

  const doAiDraft = async () => {
    if (aiBusy.draft) return;
    // Warn if the user has content — don't clobber silently.
    const hasContent = form.matter_title || form.client_issue || form.legal_opinion || form.facts.length || form.recommended_steps.length;
    if (hasContent && !window.confirm("Generate a fresh AI draft?\n\nYour current text will be overwritten (nothing is published yet).")) return;
    setAiBusy((b) => ({ ...b, draft: true }));
    try {
      const r = await lcrAiDraft(cid);
      const p = r?.proposal || {};
      setForm((f) => ({
        ...f,
        matter_title: p.matter_title || f.matter_title,
        client_issue: p.client_issue || f.client_issue,
        facts: (p.facts && p.facts.length) ? p.facts : f.facts,
        legal_opinion: p.legal_opinion || f.legal_opinion,
        recommended_steps: (p.recommended_steps && p.recommended_steps.length) ? p.recommended_steps : f.recommended_steps,
        outcome: (p.outcome && p.outcome.length) ? p.outcome : f.outcome,
      }));
      toast.success("Draft ready — review & edit before publishing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setAiBusy((b) => ({ ...b, draft: false }));
    }
  };

  const doAiImprove = async (field) => {
    if (aiBusy.improve) return;
    const current = form[field];
    if (!current?.trim()) {
      toast.message("Write a rough version first, then Improve.");
      return;
    }
    setAiBusy((b) => ({ ...b, improve: field }));
    try {
      const r = await lcrAiImprove(cid, field, current);
      if (r?.improved) {
        setForm((f) => ({ ...f, [field]: r.improved }));
        toast.success("Rewritten — check that facts are preserved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI improve failed");
    } finally {
      setAiBusy((b) => ({ ...b, improve: null }));
    }
  };

  const doAiSuggest = useCallback(async (silent = false) => {
    if (aiBusy.suggest) return;
    setAiBusy((b) => ({ ...b, suggest: true }));
    try {
      const r = await lcrAiSuggest(cid);
      setSuggestions(r?.suggestions || []);
      if (!silent) toast.success("Suggestions updated");
    } catch (_) {
      if (!silent) toast.error("Suggestions unavailable");
    } finally {
      setAiBusy((b) => ({ ...b, suggest: false }));
    }
  }, [cid, aiBusy.suggest]);

  const doAiImproveList = async (field) => {
    if (aiBusy.improve) return;
    const items = form[field] || [];
    if (!items.length) {
      toast.message("Add a few bullets first, then Improve.");
      return;
    }
    setAiBusy((b) => ({ ...b, improve: field }));
    try {
      const r = await lcrAiImprove(cid, field, items.join("\n"));
      const list = r?.improved_list || (r?.improved || "").split("\n").map((s) => s.trim()).filter(Boolean);
      if (list.length) {
        setForm((f) => ({ ...f, [field]: list }));
        toast.success("Rewritten — check that facts are preserved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI improve failed");
    } finally {
      setAiBusy((b) => ({ ...b, improve: null }));
    }
  };

  // ---------- attachments ----------

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10MB"); return; }
    setUploading(true);
    try {
      const r = await uploadLcrAttachment(cid, file);
      setAttachments((a) => [...a, r.attachment]);
      if (lcr?.state === "published") setLcr((l) => ({ ...l, state: r.lcr_state || "draft" }));
      toast.success("Attachment uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (aid) => {
    try {
      await removeLcrAttachment(cid, aid);
      setAttachments((a) => a.filter((x) => x.id !== aid));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove attachment");
    }
  };

  // ---------- templates ----------

  const applyTemplate = (t) => {
    if (!window.confirm(`Apply template "${t.name}"?\n\nSections present in the template will replace what's on screen.`)) return;
    setForm((f) => ({
      ...f,
      ...t.fields,
      follow_up: { recommended: false, after: "", after_date: "", note: "", ...(t.fields.follow_up || f.follow_up) },
    }));
    toast.success("Template applied — review before publishing");
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name (visible only to you):", form.matter_title || "");
    if (!name || !name.trim()) return;
    try {
      const r = await saveLcrTemplate({ name: name.trim(), fields: form });
      setTemplates((ts) => [r.template, ...ts]);
      toast.success("Saved to your templates");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    }
  };

  const deleteTemplateById = async (tid) => {
    try {
      await deleteLcrTemplate(tid);
      setTemplates((ts) => ts.filter((t) => t.id !== tid));
      toast.success("Template deleted");
    } catch {
      toast.error("Could not delete template");
    }
  };

  // ---------- save + publish ----------

  const saveDraft = async () => {
    setSaving(true);
    try {
      const data = await patchLcr(cid, form);
      setLcr(data.lcr);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const openPreCheck = async () => {
    try {
      // Save latest form first so the readiness report reflects on-screen state.
      await patchLcr(cid, form).catch(() => {});
      const r = await lcrReadiness(cid);
      setCheckReport(r);
      setShowPreCheck(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run pre-publish check");
    }
  };

  const openPreview = async () => {
    try {
      const r = await fetchLcrPreview(cid);
      setPreviewData(r.preview);
      setShowPreCheck(false);
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preview");
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const data = await publishLcr(cid);
      setLcr(data.lcr);
      setForm(buildForm(data.lcr));
      setAttachments(data.lcr?.attachments || attachments);
      setShowPreCheck(false);
      setShowPreview(false);
      toast.success(`Published as Version ${data.lcr.version}`);
      fetchLcrVersions(cid).then((v) => setVersions(v.items || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally {
      setPublishing(false);
    }
  };

  const requestAccess = async (row) => {
    if (row.request_state === "pending") {
      toast.message("Request already pending — waiting for client");
      return;
    }
    if (row.request_state === "denied") {
      toast.message("Client denied access");
      return;
    }
    try {
      const r = await requestLcrAccess({ lcr_id: row.lcr_id, consultation_id: cid });
      const st = r?.request?.state;
      if (st === "always_allow") toast.success("Access granted automatically");
      else if (st === "denied") toast.message("Client's privacy setting denies sharing");
      else toast.success("Request sent to client");
      const p = await fetchPreviousRecords(cid);
      setPreviousCtx({ granted: p.granted || [], available: p.available || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filledCount = useMemo(() => {
    return [
      !!form.matter_title.trim(),
      !!form.client_issue.trim(),
      form.facts.length > 0,
      !!form.legal_opinion.trim(),
      form.recommended_steps.length > 0,
      form.outcome.length > 0,
    ].filter(Boolean).length;
  }, [form]);

  if (state.loading) return <div className="p-10 text-center text-slate-400" data-testid="lcr-loading">Loading Legal Consultation Record…</div>;
  if (state.error) return <div className="p-10 text-center text-rose-500" data-testid="lcr-error">{state.error}</div>;

  return (
    <div data-testid="lawyer-lcr-page" className="max-w-3xl mx-auto">
      <Link to="/lawyer-portal/consultations?bucket=completed" className="text-xs text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1">
        <ArrowLeft size={12}/> Back to consultations
      </Link>

      {/* Header */}
      <div className="mt-3 flex items-baseline gap-3 justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Legal Consultation Record</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] mt-1" style={{ fontFamily: "Poppins" }}>
            {form.matter_title || "New Record"}
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            {consultation?.user_name || "Client"} · {consultation?.consultation_type || "chat"} on {consultation?.slot_date || (consultation?.completed_at || "").slice(0, 10)}
          </p>
        </div>
        <StateBadge state={lcr?.state} version={lcr?.version} />
      </div>

      {/* AI Draft banner */}
      <div className="mt-5 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#173a63] text-white p-4 sm:p-5 flex flex-wrap items-center gap-3 shadow-sm" data-testid="ai-draft-banner">
        <div className="h-11 w-11 rounded-2xl bg-[#D4AF37]/20 grid place-items-center shrink-0">
          <Sparkles size={20} className="text-[#D4AF37]"/>
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-extrabold">Skip the typing — let AI draft this for you</p>
          <p className="text-xs opacity-80 mt-0.5">One click reads the consultation context and pre-fills every section. You review and edit before publishing.</p>
        </div>
        <button
          onClick={doAiDraft}
          disabled={aiBusy.draft}
          className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A2540] text-sm font-bold hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
          data-testid="ai-generate-draft-btn"
        >
          {aiBusy.draft ? <><Loader2 size={14} className="animate-spin"/> Drafting…</> : <><Wand2 size={14}/> Generate Draft</>}
        </button>
      </div>

      {/* Personal templates */}
      {templates.length > 0 && (
        <TemplateBar templates={templates} onApply={applyTemplate} onDelete={deleteTemplateById} />
      )}

      {/* Previous records context (client-controlled) */}
      {previousCtx.available.length > 0 && (
        <PreviousRecordsPanel granted={previousCtx.granted} available={previousCtx.available} onRequest={requestAccess}/>
      )}

      {/* AI Suggestions (side panel — collapsible) */}
      <SuggestionsPanel
        open={showSuggest}
        onToggle={() => setShowSuggest((v) => !v)}
        items={suggestions}
        onLoad={() => doAiSuggest()}
        loading={aiBusy.suggest}
      />

      {/* --- FLAT FORM: single vertical stack --- */}
      <div className="mt-6 space-y-5">
        <Row label="Matter Title" hint="Client-visible short label (e.g., Employment Termination, Cheque Bounce).">
          <div className="relative">
            <input
              data-testid="lcr-matter-title"
              className="w-full h-11 rounded-xl border border-slate-200 px-3 pr-24 text-sm font-semibold focus:outline-none focus:border-[#0A2540]"
              value={form.matter_title}
              onChange={(e) => setForm({ ...form, matter_title: e.target.value })}
              placeholder="e.g., Employment Termination"
              maxLength={200}
            />
            <ImproveButton loading={aiBusy.improve === "matter_title"} onClick={() => doAiImprove("matter_title")} />
          </div>
        </Row>

        <Row label="Consultation Summary" hint="What legal issue did the client seek advice on?">
          <RichArea value={form.client_issue} onChange={(v) => setForm({ ...form, client_issue: v })} rows={4} testid="lcr-client-issue" placeholder="Summarise the legal issue in the client's words." />
          <ImproveButton block loading={aiBusy.improve === "client_issue"} onClick={() => doAiImprove("client_issue")}/>
        </Row>

        <Row label="Facts Presented" hint="Structured bullets of the key facts.">
          <BulletList value={form.facts} onChange={(v) => setForm({ ...form, facts: v })} placeholder="Add a fact and press Enter" testid="lcr-facts" />
          <ImproveButton block loading={aiBusy.improve === "facts"} onClick={() => doAiImproveList("facts")}/>
        </Row>

        <Row label="Legal Advice" hint="Your professional opinion in plain, client-readable language.">
          <RichArea value={form.legal_opinion} onChange={(v) => setForm({ ...form, legal_opinion: v })} rows={6} testid="lcr-legal-opinion" placeholder="Summarise your professional legal opinion." />
          <ImproveButton block loading={aiBusy.improve === "legal_opinion"} onClick={() => doAiImprove("legal_opinion")}/>
        </Row>

        <Row label="Documents Reviewed" hint="Reference the documents you inspected during the consultation.">
          <DocList value={form.documents_reviewed} onChange={(v) => setForm({ ...form, documents_reviewed: v })} testid="lcr-documents" />
        </Row>

        <Row label="Attachments" hint="Upload supporting documents — draft notices, legal opinions, agreements. These are shared with the client in the published record.">
          <AttachmentsBlock
            attachments={attachments}
            uploading={uploading}
            onPick={() => fileInputRef.current?.click()}
            onRemove={handleRemoveAttachment}
            isPublished={isPublished}
          />
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
            onChange={handleUpload}
            data-testid="lcr-attachment-input"
          />
        </Row>

        <Row label="Recommended Next Steps" hint="A checklist of what the client should do next.">
          <StepList value={form.recommended_steps} onChange={(v) => setForm({ ...form, recommended_steps: v })} presets={NEXT_STEP_PRESETS} testid="lcr-steps" />
          <ImproveButton block loading={aiBusy.improve === "recommended_steps"} onClick={() => doAiImproveList("recommended_steps")}/>
        </Row>

        <Row label="Follow-up Consultation" hint="Is another consultation recommended?">
          <FollowUpBlock value={form.follow_up} onChange={(v) => setForm({ ...form, follow_up: v })} />
        </Row>

        <Row label="Consultation Outcome" hint="What happened here? (This does NOT create a case.)">
          <OutcomePicker value={form.outcome} onChange={(v) => setForm({ ...form, outcome: v })} />
        </Row>

        <Row label="Internal Lawyer Notes" hint="Private. Never shared with the client. Never included in the PDF." warn>
          <RichArea value={form.internal_notes} onChange={(v) => setForm({ ...form, internal_notes: v })} rows={3} testid="lcr-internal" placeholder="Tactical notes, sensitivities, reminders…" />
        </Row>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-4 mt-8 flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-lg" data-testid="lcr-actions">
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="text-xs font-bold text-slate-600 hover:text-[#0A2540] px-3 py-2 rounded-full border border-slate-200 inline-flex items-center gap-1"
          data-testid="lcr-history-toggle"
        >
          <History size={14}/> {versions.length} version{versions.length === 1 ? "" : "s"}
        </button>
        <div className="flex-1 text-xs text-slate-500 truncate">
          {isPublished
            ? `You are editing a new draft → publish saves v${(lcr?.version || 1) + 1}`
            : `${filledCount}/6 sections filled`}
        </div>
        <button
          onClick={saveTemplate}
          className="px-4 py-2 text-xs font-bold rounded-full border border-slate-200 hover:border-[#0A2540] inline-flex items-center gap-1"
          data-testid="lcr-save-template"
          title="Save this structure as a private template for similar matters"
        >
          <BookmarkPlus size={14}/> Save as Template
        </button>
        <button
          onClick={saveDraft}
          disabled={saving}
          className="px-4 py-2 text-xs font-bold rounded-full border border-slate-200 hover:border-[#0A2540] disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="lcr-save-draft"
        >
          <Save size={14}/> {saving ? "Saving…" : "Save Draft"}
        </button>
        <button
          onClick={openPreCheck}
          disabled={publishing}
          className="px-4 py-2 text-xs font-bold rounded-full inline-flex items-center gap-1 bg-[#0A2540] text-[#D4AF37] hover:opacity-90 disabled:opacity-60"
          data-testid="lcr-publish"
        >
          <Send size={14}/> Publish
        </button>
      </div>

      {showHistory && (
        <div className="mt-4 border border-slate-200 rounded-2xl p-4 bg-white" data-testid="lcr-history-panel">
          <h4 className="text-sm font-bold text-[#0A2540] mb-2">Version history</h4>
          {versions.length === 0 && <p className="text-xs text-slate-400">No published versions yet.</p>}
          <ul className="text-xs divide-y divide-slate-100">
            {versions.map((v) => (
              <li key={v.snapshot_id} className="py-2 flex justify-between gap-2">
                <span className="font-semibold text-slate-700">v{v.version}</span>
                <span className="text-slate-500 truncate">{v.matter_title}</span>
                <span className="text-slate-400 shrink-0">{(v.published_at || "").slice(0, 19).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showPreCheck && checkReport && (
        <PreCheckModal report={checkReport} onCancel={() => setShowPreCheck(false)} onContinue={openPreview}/>
      )}

      {showPreview && previewData && (
        <PreviewModal
          preview={previewData}
          attachments={attachments}
          onBack={() => setShowPreview(false)}
          onPublish={doPublish}
          publishing={publishing}
        />
      )}
    </div>
  );
}
